import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { DashboardLayout } from '../../components/dashboard';
import { WikiPageDisplay } from '../../components/wiki/WikiPageDisplay';
import { WikiPageHistory } from '../../components/wiki/WikiPageHistory';
import { wikiService } from '../../services/wikiService';
import { WikiPage, WikiPageHistory as WikiPageHistoryType } from '../../types/wiki';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, Page, Skeleton } from '../../components/ui';
import { ConfidentialityPanel } from '../../features/confidentiality';
import { asFlag, useUrlState } from '../../hooks/useUrlState';

export const WikiViewPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
  const [wikiPage, setWikiPage] = useState<WikiPage | null>(null);
  const [history, setHistory] = useState<WikiPageHistoryType[]>([]);
  // In the URL so a version can be linked to, not only clicked to
  const [showHistory, setShowHistory] = useUrlState('history', false, asFlag);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (!id) return;
        setLoading(true);
        const data = await wikiService.getWikiPage(id);
        setWikiPage(data);
      } catch (err) {
        setError('Failed to load wiki page');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // History loads whenever it is on screen — whether it was opened here or
  // arrived already open in the address bar
  useEffect(() => {
    if (!showHistory || !id) return;
    let alive = true;
    setHistoryLoading(true);
    wikiService
      .getWikiPageHistory(id)
      .then((h) => { if (alive) setHistory(h); })
      .catch(() => {
        if (!alive) return;
        setShowHistory(false);
        setError('Failed to load history');
      })
      .finally(() => { if (alive) setHistoryLoading(false); });
    return () => { alive = false; };
  }, [showHistory, id, setShowHistory]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      await wikiService.deleteWikiPage(id);
      navigate('/wiki');
    } catch (err: any) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete page');
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <Page>
        <div className="mx-auto max-w-[976px]">
          <Link
            to="/wiki"
            className="-ml-1 mb-2 inline-flex h-7 items-center gap-0.5 rounded-full pl-0.5 pr-2 text-[13px] text-neutral-500 transition-colors hover:text-ink"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            Wiki
          </Link>

          {loading ? (
            <div aria-hidden="true">
              <Skeleton className="h-7 w-2/3 max-w-md" />
              <Skeleton className="mt-3 h-4 w-48" />
              <div className="mt-8 max-w-[720px] space-y-3 border-t border-neutral-200/70 pt-6">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="mt-6 h-5 w-40" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          ) : error ? (
            <p role="alert" className="mt-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>
          ) : wikiPage ? (
            <>
              <WikiPageDisplay
                wikiPage={wikiPage}
                onEdit={() => id && navigate(`/wiki/${id}/edit`)}
                onViewHistory={() => setShowHistory(true)}
                onDelete={isAdmin ? () => { setDeleteError(null); setConfirmDelete(true); } : undefined}
                deleteDisabled={wikiPage.isLocked}
              />
              <ConfidentialityPanel
                className="mt-6"
                entityType="wiki_page"
                entityId={wikiPage.id}
                onChanged={(summary) =>
                  setWikiPage((page) =>
                    page
                      ? {
                          ...page,
                          confidentiality: summary.confidentiality,
                          confidentialityNote: summary.confidentialityNote,
                        }
                      : page,
                  )
                }
              />
            </>
          ) : null}
        </div>
      </Page>

      {wikiPage && (
        <Dialog
          open={showHistory}
          onClose={() => setShowHistory(false)}
          label="Version history"
          handle
          className="max-h-[88dvh] sm:max-h-[84vh] sm:max-w-xl"
        >
          <DialogBody className="pb-5 pt-2 sm:pt-5">
            {historyLoading ? (
              <div aria-hidden="true">
                <Skeleton className="h-5 w-36" />
                <div className="mt-5 space-y-5">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="mt-1 h-2 w-2 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-40" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <WikiPageHistory
                history={history}
                currentVersion={wikiPage.version}
                onRestore={async (version) => {
                  if (!id) return;
                  const restored = await wikiService.restoreWikiPageVersion(id, version);
                  setWikiPage(restored);
                  setShowHistory(false);
                }}
                onClose={() => setShowHistory(false)}
                isLoading={false}
              />
            )}
          </DialogBody>
        </Dialog>
      )}

      <Dialog
        open={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        labelledBy="wiki-view-delete-title"
        className="sm:max-w-sm"
      >
        <DialogHeader id="wiki-view-delete-title" title="Delete page?" />
        <DialogBody>
          <p className="text-[13px] text-neutral-500">
            “<span className="text-ink">{wikiPage?.title}</span>” will be deleted permanently.
          </p>
          {deleteError && (
            <p role="alert" className="mt-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{deleteError}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
          <Button variant="primary" onClick={handleDelete} loading={deleting}>Delete</Button>
        </DialogFooter>
      </Dialog>
    </DashboardLayout>
  );
};
